import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { ReferenceListModals } from './Modals';
import { ReferenceHeader } from './ReferenceHeader';
import { Alert, Snackbar, Stack } from '@mui/material';
import { theme } from '../../legacy/style/theme';
import { useAppDispatch, useAppSelector } from '../../../types';
import { InitReferenceCommand } from '../../../core/reference/commands/InitReferenceCommand';
import { DataGridPro, GridCellEditStopReasons, useGridApiRef } from '@mui/x-data-grid-pro';
import { getDataGridColumns } from './getDataGridColumns';
import { useTranslation } from 'react-i18next';
import {
    roadSectionsPaginationQuery,
    roadSectionsQuery,
    loadingRoadSectionsQuery,
    roadSectionsColumnVisibilityQuery,
    roadSectionsFilterQuery,
} from '../../../core/reference/queries';
import FilterReferenceCommand from '../../../core/reference/commands/FilterReferenceCommand';
import localeTexts from '../../../providers/translations/dataGridFilterTranslations';
import { gridStyle } from '../../style/datagrid';
import CustomToolbar from '../../session/list-sessions/CustomToolbar';
import CustomPagination from '../../session/list-sessions/CustomPagination';
import { setSelectedRoadSections, paginationUpdated } from '../../../core/reference/state';
import { EmptyReferenceList } from './EmptyReferenceList';
import { useReferenceGenerationPolling } from './useReferenceGenerationPolling';
import { setModalToDisplay } from '../../../core/legacy/actions/modals';
import { RoadSection } from '../../../core/domain/Reference';
import editRoadSection from '../../../core/reference/commands/EditRoadSection';
import { ReferenceModalTypes } from '../../../core/legacy/reducers/modals';
import SaveColumnsPreferenceCommand from '../../../core/reference/commands/SaveColumnsPreferenceCommand';
import LoadColumnsPreferenceCommand from '../../../core/reference/commands/LoadColumnsPreferenceCommand';

export const ReferencesListPage: FC = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const roadSections = useAppSelector(roadSectionsQuery);
    const apiRef = useGridApiRef();
    const pagination = useAppSelector(roadSectionsPaginationQuery);
    const loadingState = useAppSelector(loadingRoadSectionsQuery);
    const columnsVisibilityPref = useAppSelector(roadSectionsColumnVisibilityQuery);
    const filter = useAppSelector(roadSectionsFilterQuery);

    useReferenceGenerationPolling();

    const [locallySelectedIds, setLocallySelectedIds] = useState<string[]>([]);
    const [panelElement, setPanelElement] = useState<HTMLElement | null>(null);
    const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

    const columns = useMemo(() => getDataGridColumns(t, true), []);
    const isLoading = useMemo(() => loadingState || columns.length === 0, [loadingState, columns]);

    useEffect(() => {
        dispatch(InitReferenceCommand());
        dispatch(LoadColumnsPreferenceCommand());
    }, []);

    const handleColumnsVisibilityChange = (newPref: Record<string, boolean>) => {
        dispatch(SaveColumnsPreferenceCommand(newPref));
    };

    const handleRowEdit = useCallback(
        async (updatedRow: RoadSection, originalRow: RoadSection) => {
            try {
                await dispatch(
                    editRoadSection({
                        roadSectionData: updatedRow,
                        roadSectionId: updatedRow.id,
                    }),
                ).unwrap();

                return updatedRow;
            } catch (err: any) {
                if (err.name === 'ReferenceDataValidationError') {
                    setSnackbarMessage(t(`reference.error.${err.message}`));
                }
                if (err.name === 'ReferenceDataEditionError') {
                    setSnackbarMessage(t('reference.error.default'));
                }

                dispatch(setModalToDisplay(ReferenceModalTypes.ROAD_SECTION_EDIT_FAILURE));

                return originalRow;
            }
        },
        [dispatch],
    );

    return (
        <Stack sx={{ width: '98%', height: '100%' }}>
            <ReferenceHeader />
            <Stack
                sx={{
                    height: 'calc(100vh - 226px)',
                    padding: '10px 20px 0',
                    borderRadius: '6px',
                    boxShadow: theme.boxShadow.grey.thin,
                    backgroundColor: theme.color.white.main,
                }}
            >
                <DataGridPro
                    columns={columns}
                    apiRef={apiRef}
                    loading={isLoading}
                    rows={roadSections}
                    sx={gridStyle}
                    slots={{
                        noRowsOverlay: EmptyReferenceList,
                        toolbar: CustomToolbar,
                    }}
                    slotProps={{
                        panel: { anchorEl: panelElement },
                        toolbar: { setPanelElement },
                    }}
                    localeText={localeTexts}
                    filterModel={filter}
                    onFilterModelChange={(m) => dispatch(FilterReferenceCommand(m))}
                    onStateChange={({ filter: { filteredRowsLookup }, rowSelection }) => {
                        const selectedRoadSectionsMatchingFilters =
                            Object.keys(filteredRowsLookup).length > 0
                                ? rowSelection.filter((id: string) => filteredRowsLookup[id])
                                : rowSelection;

                        dispatch(setSelectedRoadSections(selectedRoadSectionsMatchingFilters));
                    }}
                    rowSelectionModel={locallySelectedIds}
                    processRowUpdate={handleRowEdit}
                    getRowId={(row) => row.id}
                    onRowSelectionModelChange={(m) => setLocallySelectedIds(m as string[])}
                    pagination
                    paginationModel={pagination}
                    onPaginationModelChange={(newPagination) => {
                        if (!isLoading) {
                            dispatch(paginationUpdated(newPagination));
                        }
                    }}
                    pageSizeOptions={[25, 50, 100, 500, 1000]}
                    columnVisibilityModel={columnsVisibilityPref}
                    onColumnVisibilityModelChange={handleColumnsVisibilityChange}
                    onCellEditStop={({ field, id, reason }, event) => {
                        if (reason === GridCellEditStopReasons.escapeKeyDown) {
                            event.defaultMuiPrevented = true;
                            apiRef.current.stopCellEditMode({ id, field, ignoreModifications: true });
                        }
                    }}
                    checkboxSelection
                    hideFooter
                    hideFooterPagination
                />
            </Stack>
            <CustomPagination
                label="tronçon"
                pluralLabel="tronçons"
                apiRef={apiRef}
                onPaginationChange={(newPagination) => {
                    if (!isLoading) {
                        dispatch(paginationUpdated(newPagination));
                    }
                }}
            />
            <ReferenceListModals />
            {snackbarMessage && (
                <Snackbar
                    open
                    autoHideDuration={3000}
                    onClose={() => setSnackbarMessage(null)}
                    message={snackbarMessage}
                >
                    <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            )}
        </Stack>
    );
};
